import React, { Component, PropsWithChildren } from 'react';
import { ExternalClickWrapper } from '../ExternalClickWrapper/ExternalClickWrapper';
import { classAttr } from '../Modal/utils';
import { IInputFieldProps } from './types';

const CODE = {
  ENTER: 13,
  ESC: 27,
  SPACE: 32,
  TAB: 9,
};

interface IInputFieldState {
  value: string;
}

export class InputField extends Component<PropsWithChildren<IInputFieldProps>, IInputFieldState> {
  public static defaultProps = {
    onBlur: () => {
      /* noop */
    },
    value: '',
  };
  public state = { value: '', width: 150 };
  private elInput: HTMLInputElement | null;
  private containerWidth: number = 50;
  private fontSize: number;
  private span: HTMLSpanElement;

  public componentDidMount() {
    const { elInput } = this;
    const { value } = this.props;

    // find container width which is used to constrain the width of our input field
    if (elInput) {
      const container = classAttr.findAncestor(elInput, 'nl-container') as HTMLElement;
      const parent = container.parentElement as HTMLElement;
      this.containerWidth = Math.round(parent.getBoundingClientRect().width);
    }

    this.setState({ value }, () => {
      // pre-select the entire string in the input field
      elInput && elInput.setSelectionRange(0, value.length);
    });
  }

  public componentWillUnmount() {
    if (this.span) {
      document.body.removeChild(this.span);
    }
  }

  public keyboardListener = (e: KeyboardEvent | React.KeyboardEvent<HTMLInputElement>) => {
    if ([CODE.ENTER, CODE.TAB].includes(e.keyCode)) {
      this.props.onChange(this.props.name, this.state.value);
      e.preventDefault();
      e.stopPropagation();
    }
  };

  public render() {
    const { align = 'left', fontSize, labeledById, name, onBlur, placeholder } = this.props;
    const width = Math.min(this.cssWidth(), this.containerWidth);
    return (
      <>
        {/* this inline-block mirrors the width/height of the absolute block that follows in 
        order to simulate occupying space that the absolutely positioned nl-text-input-wrapper would occupy
        if it wasn't absolutely positioned.
        */}
        <div className="nl-text-spacer" style={{ fontSize, width }}>
          {this.state.value || placeholder}
        </div>
        <div className={`nl-text-input-wrapper align-${align}`} aria-labelledby={labeledById} style={{ width }}>
          <ExternalClickWrapper
            onExternal={(e: any) => {
              // if user hits ESC key then we revert to the original value that was passed in
              const val = e.keyCode && e.keyCode === CODE.ESC ? this.props.value : this.state.value;
              this.props.onChange(name, val);
            }}
          >
            <input
              type={'text'}
              name={name}
              style={{ fontSize }}
              ref={(e) => {
                e && e.focus();
                this.elInput = e;
              }}
              placeholder={placeholder}
              value={this.state.value}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={this.keyboardListener} // listening for TAB key
              onBlur={onBlur}
              onChange={(e) => {
                this.setState({ value: e.target.value });
              }}
            />
          </ExternalClickWrapper>
        </div>
      </>
    );
  }

  private cssWidth() {
    const { placeholder = '' } = this.props;
    let fontSize: string | number = 14;

    if (this.elInput) {
      fontSize = window.getComputedStyle(this.elInput, null).getPropertyValue('font-size');
      fontSize = parseFloat(fontSize);
    }

    // create a span to measure the width of the chars in the input field
    if (!this.span) {
      this.span = createTemporarySpan();
    }

    if (this.fontSize !== fontSize) {
      this.span.style.fontSize = fontSize + 'px';
      this.fontSize = fontSize;
    }

    // add 2 '__' chars of width for padding
    this.span.innerText = (this.state.value || placeholder) + '__';
    const width = this.span.getBoundingClientRect().width;

    return width;
  }
}

function createTemporarySpan() {
  const span = document.createElement('span');
  span.style.visibility = 'hidden';
  span.setAttribute('aria-hidden', 'true');
  document.body.appendChild(span);
  return span;
}
